<template>
  <div class="onboarding" role="dialog" aria-modal="true" aria-labelledby="onboarding-title">
    <div class="onboarding__card" data-test="onboarding-modal">
      <div class="onboarding__eyebrow">Welcome</div>
      <h2 id="onboarding-title" class="onboarding__title">Let's get started!</h2>

      <div v-for="question in questions" :key="question.key" class="onboarding__q">
        <div class="onboarding__label">{{ question.label }}</div>
        <div class="onboarding__chips">
          <button
            v-for="option in question.options"
            :key="option.label"
            type="button"
            class="onboarding__chip"
            :class="{ 'is-on': answers[question.key] === option.label }"
            :data-test="`onboarding-${question.key}`"
            @click="answers[question.key] = option.label"
          >{{ option.label }}</button>
        </div>
        <div v-if="question.key === 'start'" class="onboarding__pilot">
          <div class="onboarding__pilot-icon"><span class="material-icons-outlined">chat_bubble_outline</span></div>
          <div><b>Not sure? Don't worry.</b> Pick anything, or skip. Once you're in, just tell <b>POP Pilot</b> what you're trying to do in plain words and it will take you to the right tool.</div>
        </div>
      </div>

      <div v-if="error" class="pop-auth__box pop-auth__box--danger" style="margin-bottom: 12px">{{ error }}</div>
      <div class="onboarding__actions">
        <button type="button" class="pop-auth__btn pop-auth__btn--primary onboarding__continue" :disabled="busy || !answers.start" data-test="onboarding-continue" @click="finish(false)">
          {{ busy ? 'Saving' : tool ? `Open ${toolNames[tool]}` : 'Continue' }}
        </button>
        <button type="button" class="pop-auth__link" :disabled="busy" data-test="onboarding-skip" @click="finish(true)">Skip</button>
      </div>
    </div>
  </div>
</template>

<script>
import { segmentTrack } from '@/utils'

export const START_OPTIONS = [
  { label: 'Optimize an existing page', tool: 'onpage' },
  { label: 'Write new content with AI', tool: 'aiwriter' },
  { label: 'Research keywords for a site', tool: 'keyword' },
  { label: 'See how AI talks about a brand', tool: 'aivis' },
  { label: 'Just exploring', tool: null },
]

export const TOOL_NAMES = {
  onpage: 'On Page Optimization', aiwriter: 'AI Writer', keyword: 'Keyword Research', aivis: 'AI Visibility',
}

// First-visit questions (user.welcomeCompleted false). Saves the answers through
// POST /api/users/welcome/ and emits `done` with the tool to start on; App.vue routes.
export default {
  name: 'OnboardingModal',
  data() {
    return {
      busy: false,
      error: '',
      answers: { role: null, source: null, start: null },
      toolNames: TOOL_NAMES,
      questions: [
        { key: 'role', label: 'Tell us about you', options: [
          'SEO freelancer or consultant', 'In-house marketer', 'Agency', 'Business owner', 'Content writer',
        ].map((label) => ({ label })) },
        { key: 'source', label: 'How did you hear about POP?', options: [
          'YouTube', 'Google search', 'A friend or colleague', 'Podcast or course', 'Other',
        ].map((label) => ({ label })) },
        { key: 'start', label: 'Where do you want to start?', options: START_OPTIONS },
      ],
    }
  },
  computed: {
    tool() {
      const picked = START_OPTIONS.find((o) => o.label === this.answers.start)
      return picked ? picked.tool : null
    },
  },
  methods: {
    async finish(skipped) {
      if (this.busy) return
      this.busy = true
      this.error = ''
      const payload = skipped
        ? { skipped: true }
        : { role: this.answers.role, source: this.answers.source, start: this.answers.start, skipped: false }
      try {
        const response = await this.$store.dispatch('saveWelcomeAnswers', payload)
        const data = response.data || {}
        if (data.status !== 'SUCCESS') {
          this.error = data.msg || 'Could not save your answers. Please try again.'
          return
        }
        if (data.user) this.$store.commit('setUser', data.user)
        segmentTrack(skipped ? 'onboarding_skipped' : 'onboarding_completed', { ...payload, tool: skipped ? null : this.tool })
        this.$emit('done', { tool: skipped ? null : this.tool, skipped })
      } catch (e) {
        console.log(e)
        this.error = 'Could not save your answers. Please try again.'
      } finally {
        this.busy = false
      }
    },
  },
}
</script>

<style scoped lang="scss">
@import './auth-tokens.scss';

.onboarding {
  position: fixed; inset: 0; z-index: 120;
  background: rgba(1, 71, 81, 0.45); backdrop-filter: blur(4px);
  display: flex; align-items: flex-start; justify-content: center; padding: 40px 20px; overflow-y: auto;

  &__card {
    width: 560px; max-width: 100%; margin: auto 0;
    background: #fff; color: $pop-teal; border-radius: 18px; padding: 32px;
    box-shadow: 0 24px 60px -24px rgba(1, 71, 81, 0.35);
  }
  &__eyebrow { font-size: 12px; font-weight: 700; color: $pop-accent; letter-spacing: 0.06em; text-transform: uppercase; margin-bottom: 10px; }
  &__title { font-family: 'Mulish', sans-serif; font-weight: 800; font-size: 26px; letter-spacing: -0.03em; line-height: 1.1; margin: 0 0 20px; color: $pop-teal; text-align: center; }
  &__q { margin-bottom: 18px; }
  &__label { font-size: 13.5px; font-weight: 700; margin-bottom: 8px; }
  &__chips { display: flex; flex-wrap: wrap; gap: 8px; }
  &__chip {
    background: #fff; color: $pop-teal; border: 1px solid $pop-field-border; border-radius: 999px;
    font-family: inherit; font-weight: 600; font-size: 13px; padding: 8px 14px; cursor: pointer;
    &.is-on { background: $pop-teal; color: #fff; border-color: $pop-teal; }
  }
  &__pilot {
    display: flex; gap: 12px; align-items: flex-start; margin-top: 12px; padding: 12px 14px;
    background: #fff3ec; border-radius: 12px; font-size: 13px; line-height: 1.5;
  }
  &__pilot-icon {
    width: 32px; height: 32px; border-radius: 10px; background: $pop-accent; color: #fff; flex: none;
    display: flex; align-items: center; justify-content: center;
    .material-icons-outlined { font-size: 18px; }
  }
  &__actions { display: flex; gap: 14px; align-items: center; margin-top: 8px; }
  &__continue { width: auto; margin-top: 0; padding: 12px 22px; font-size: 14px; }
}

@media (max-width: 600px) {
  .onboarding { padding: 16px; }
  .onboarding__card { padding: 22px 18px; }
}
</style>
